import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import { useNespresso } from "../context/NespressoContext";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  black: "#000000",
  darkGray: "#292929",
  lightGray: "#f9f9f9",
  border: "#e6e5e4",
};

const SummaryView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { state: nespressoState, currentTier, nextTier, progressToNext, podsToNext } = useNespresso();

  // Calculate totals from empties
  const totalCredit = state.empties.reduce((sum, item) => {
    const dollarValue = item.product.rewards?.dollarValue;
    if (dollarValue) {
      return sum + parseFloat(dollarValue.replace("$", ""));
    }
    return sum;
  }, 0);

  const totalPoints = state.empties.reduce((sum, item) => {
    return sum + (item.product.rewards?.loyaltyPoints || 0) * item.quantity;
  }, 0);

  // Get capsule count from empties
  const capsuleCount = state.empties.length;

  // Calculate environmental impact
  const aluminumSaved = capsuleCount * 0.8; // grams per capsule
  const co2Saved = capsuleCount * 0.015; // kg per capsule

  const handleConfirm = () => {
    navigate("/nespresso/offer");
  };

  const handleBack = () => {
    navigate("/nespresso/reward-selection");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span
            className="font-medium uppercase tracking-widest text-sm"
            style={{ color: NESPRESSO.gold }}
          >
            Nespresso Recycling Program
          </span>
        </div>
        <div className="w-20" /> {/* Spacer for alignment */}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-light text-white mb-3">
            Your Recycling Summary
          </h1>
          <p className="text-lg text-white/60 font-light">
            Review your contribution and rewards
          </p>
        </motion.div>

        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-lg p-8 max-w-lg w-full shadow-2xl mb-8"
        >
          {/* Capsules & Points */}
          <div
            className="flex items-center justify-between pb-6 mb-6"
            style={{ borderBottom: `1px solid ${NESPRESSO.border}` }}
          >
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-1">Capsules</p>
              <p
                className="text-4xl font-medium"
                style={{ color: NESPRESSO.black }}
              >
                {capsuleCount}
              </p>
            </div>
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${NESPRESSO.gold}20` }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: NESPRESSO.gold }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-1">Points</p>
              <p
                className="text-4xl font-medium"
                style={{ color: NESPRESSO.brown }}
              >
                {totalPoints.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Credit Value */}
          <div className="text-center mb-6">
            <p className="text-gray-500 text-sm mb-1">Credit Value</p>
            <p
              className="text-3xl font-medium"
              style={{ color: NESPRESSO.gold }}
            >
              ${totalCredit.toFixed(2)}
            </p>
          </div>

          {/* Environmental Impact */}
          <div
            className="rounded-lg p-4 mb-6"
            style={{ backgroundColor: NESPRESSO.lightGray }}
          >
            <p
              className="text-sm font-medium mb-3 text-center"
              style={{ color: NESPRESSO.brown }}
            >
              Environmental Impact
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-gray-500 text-xs">Aluminum Saved</p>
                <p
                  className="font-medium"
                  style={{ color: NESPRESSO.black }}
                >
                  {aluminumSaved.toFixed(0)}g
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">CO₂ Avoided</p>
                <p
                  className="font-medium"
                  style={{ color: NESPRESSO.black }}
                >
                  {co2Saved.toFixed(2)}kg
                </p>
              </div>
            </div>
          </div>

          {/* Tier Progress */}
          <div
            className="pt-6"
            style={{ borderTop: `1px solid ${NESPRESSO.border}` }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-sm">
                {currentTier.name}
              </span>
              {nextTier && (
                <span className="text-gray-500 text-sm">
                  {nextTier.name}
                </span>
              )}
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${progressToNext}%`,
                  backgroundColor: NESPRESSO.gold,
                }}
              />
            </div>
            <p className="text-center text-gray-500 text-xs">
              {nespressoState.gamification.lifetimePods} lifetime pods •{" "}
              {podsToNext > 0
                ? `${podsToNext} to next tier`
                : "Max tier reached!"}
            </p>
          </div>
        </motion.div>

        {/* Items Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="max-w-lg w-full mb-8"
        >
          <h3 className="text-white font-medium mb-4">Items Recycled</h3>
          <div className="space-y-2">
            {state.empties.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3 border border-white/10"
              >
                <span className="text-white/80 font-light">{item.product.name}</span>
                <span style={{ color: NESPRESSO.gold }}>
                  {item.product.rewards?.dollarValue || "$0.00"}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Confirm Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          onClick={handleConfirm}
          className="px-12 py-4 text-white font-medium text-lg transition-colors"
          style={{
            backgroundColor: NESPRESSO.brown,
            borderRadius: "3px",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = NESPRESSO.brownHover)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = NESPRESSO.brown)
          }
        >
          Continue to Exclusive Offers
        </motion.button>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10 font-light">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default SummaryView;
